import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard, useWriteForbiddenGuard } from '../api/guard'
import { titledErrorMessage } from '../api/problems'
import {
  HOSTS_QUERY_KEY,
  UPSTREAMS_QUERY_KEY,
  fmtTime,
  type AuthScheme,
  type ResyncAllResult,
  type Transport,
  type Upstream,
  type UpstreamHost,
  type UpstreamWrite,
} from '../api/mcpUpstreams'
import { ADMIN_CONNECTORS_QUERY_KEY } from '../api/categories'
import { ConfirmDialog } from '../components/ConfirmDialog'
import './screens.css'

const SLUG_RE = /^[a-z][a-z0-9]{1,23}$/

type Registration = 'auto' | 'dcr' | 'preregistered' | 'cimd'
type TokenAuth = '' | 'client_secret_post' | 'client_secret_basic' | 'none'

interface RegisterForm {
  slug: string
  display_name: string
  url: string
  transport: Transport
  auth_scheme: AuthScheme
  api_key_header: string
  category: string
  sync_credential: string
  tos_url: string
  tos_note: string
  connector_name: string
  oauth_registration: Registration
  oauth_client_id: string
  oauth_client_secret: string
  oauth_token_auth: TokenAuth
  oauth_scopes: string
}

const EMPTY_FORM: RegisterForm = {
  slug: '',
  display_name: '',
  url: '',
  transport: 'streamable_http',
  auth_scheme: 'bearer',
  api_key_header: '',
  category: '',
  sync_credential: '',
  tos_url: '',
  tos_note: '',
  connector_name: '',
  oauth_registration: 'auto',
  oauth_client_id: '',
  oauth_client_secret: '',
  oauth_token_auth: '',
  oauth_scopes: '',
}

/** POST /admin/connectors/mcp body: required fields + only the optionals that are set. */
function registerBody(f: RegisterForm): Record<string, unknown> {
  const body: Record<string, unknown> = {
    slug: f.slug.trim(),
    display_name: f.display_name.trim(),
    url: f.url.trim(),
    transport: f.transport,
    auth_scheme: f.auth_scheme,
    category: f.category.trim(),
  }
  if (f.auth_scheme === 'api_key') body.api_key_header = f.api_key_header.trim()
  if (f.sync_credential) body.sync_credential = f.sync_credential
  if (f.tos_url.trim()) body.tos_url = f.tos_url.trim()
  if (f.tos_note.trim()) body.tos_note = f.tos_note.trim()
  if (f.connector_name.trim()) body.connector_name = f.connector_name.trim()
  if (f.auth_scheme === 'oauth2') {
    const oauth: Record<string, unknown> = { registration: f.oauth_registration }
    if (f.oauth_client_id.trim()) oauth.client_id = f.oauth_client_id.trim()
    if (f.oauth_client_secret) oauth.client_secret = f.oauth_client_secret
    if (f.oauth_token_auth) oauth.token_endpoint_auth_method = f.oauth_token_auth
    const scopes = f.oauth_scopes.split(/[\s,]+/).filter(Boolean)
    if (scopes.length) oauth.scopes = scopes
    body.oauth = oauth
  }
  return body
}

function HostsPanel() {
  const qc = useQueryClient()
  const [host, setHost] = useState('')
  const [note, setNote] = useState('')
  const [removing, setRemoving] = useState<string | null>(null)

  const hosts = useQuery<{ hosts: UpstreamHost[] }>({
    queryKey: HOSTS_QUERY_KEY,
    queryFn: () => apiFetch('/admin/mcp-upstream-hosts'),
  })
  useForbiddenGuard(hosts.error)

  const add = useMutation({
    mutationFn: (body: { host: string; note?: string }) =>
      apiFetch('/admin/mcp-upstream-hosts', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setHost('')
      setNote('')
      qc.invalidateQueries({ queryKey: HOSTS_QUERY_KEY })
    },
  })
  useWriteForbiddenGuard(add.error)

  const remove = useMutation({
    mutationFn: (h: string) =>
      apiFetch(`/admin/mcp-upstream-hosts/${encodeURIComponent(h)}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: HOSTS_QUERY_KEY }),
  })
  useWriteForbiddenGuard(remove.error)

  const list = hosts.data?.hosts ?? []
  return (
    <div className="scr-panel">
      <h3>Host allowlist</h3>
      <p className="scr-note">
        Exact hosts only (no wildcards). An upstream URL must be https on one of these hosts;
        removing a host makes calls to its upstreams fail closed from the next request on.
      </p>
      {hosts.isError && (hosts.error as { status?: number }).status !== 403 && (
        <p className="scr-err" role="alert">
          Failed to load the host allowlist.
        </p>
      )}
      <table className="scr-table" aria-label="Allowed hosts">
        <thead>
          <tr>
            <th>Host</th>
            <th>Note</th>
            <th>Added</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map((h) => (
            <tr key={h.host}>
              <td className="scr-mono">{h.host}</td>
              <td>{h.note ?? '—'}</td>
              <td>{fmtTime(h.added_at)}</td>
              <td>
                <button
                  type="button"
                  className="screen-btn"
                  aria-label={`Remove ${h.host}`}
                  disabled={remove.isPending}
                  onClick={() => setRemoving(h.host)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
          {!hosts.isLoading && list.length === 0 && (
            <tr>
              <td colSpan={4} className="ws-empty">
                No allowed hosts.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <form
        className="scr-actions"
        onSubmit={(e) => {
          e.preventDefault()
          add.mutate(note.trim() ? { host: host.trim(), note: note.trim() } : { host: host.trim() })
        }}
      >
        <input
          className="scr-input scr-input--narrow"
          aria-label="Host to allow"
          placeholder="mcp.vendor.com"
          value={host}
          onChange={(e) => setHost(e.target.value)}
        />
        <input
          className="scr-input scr-input--narrow"
          aria-label="Host note"
          placeholder="note (optional)"
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <button type="submit" className="screen-btn" disabled={!host.trim() || add.isPending}>
          Add host
        </button>
      </form>
      {add.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(add.error)}
        </p>
      )}
      {remove.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(remove.error)}
        </p>
      )}
      {removing && (
        <ConfirmDialog
          label="Confirm host removal"
          confirmText="Remove"
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            remove.mutate(removing)
            setRemoving(null)
          }}
        >
          <p>
            Remove <strong className="scr-mono">{removing}</strong> from the allowlist? Every
            upstream on this host fails closed from the next request on.
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}

function RegisterPanel() {
  const qc = useQueryClient()
  const [form, setForm] = useState<RegisterForm>(EMPTY_FORM)
  const [confirming, setConfirming] = useState(false)
  const set = <K extends keyof RegisterForm>(k: K, v: RegisterForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }))

  const register = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiFetch<UpstreamWrite>('/admin/connectors/mcp', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      // Clear everything — including the write-only secrets — after a successful register.
      setForm(EMPTY_FORM)
      qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY })
      qc.invalidateQueries({ queryKey: ADMIN_CONNECTORS_QUERY_KEY })
    },
  })
  useWriteForbiddenGuard(register.error)

  const valid =
    SLUG_RE.test(form.slug.trim()) &&
    form.display_name.trim() !== '' &&
    form.url.trim().startsWith('https://') &&
    form.category.trim() !== '' &&
    (form.auth_scheme !== 'api_key' || form.api_key_header.trim() !== '')

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!valid) return
    if (form.connector_name.trim()) setConfirming(true)
    else register.mutate(registerBody(form))
  }

  return (
    <div className="scr-panel">
      <h3>Register an upstream MCP server</h3>
      <p className="scr-note">
        Registered <strong>dark</strong>. Nothing is visible to tenants until a sync, a tool
        approval, a recorded vendor ToS review and a flip to live with 1–3 categories.
        Credentials are write-only: stored encrypted and never shown again.
      </p>
      <form className="scr-form" onSubmit={submit} aria-label="Register upstream">
        <label>
          Slug
          <input
            className="scr-input"
            value={form.slug}
            placeholder="linear"
            onChange={(e) => set('slug', e.target.value)}
          />
        </label>
        <label>
          Display name
          <input
            className="scr-input"
            maxLength={120}
            value={form.display_name}
            onChange={(e) => set('display_name', e.target.value)}
          />
        </label>
        <label>
          MCP URL (https, allowlisted host)
          <input
            className="scr-input"
            value={form.url}
            placeholder="https://mcp.vendor.com/mcp"
            onChange={(e) => set('url', e.target.value)}
          />
        </label>
        <label>
          Transport
          <select
            value={form.transport}
            onChange={(e) => set('transport', e.target.value as Transport)}
          >
            <option value="streamable_http">streamable_http</option>
            <option value="sse">sse</option>
          </select>
        </label>
        <label>
          Auth scheme
          <select
            value={form.auth_scheme}
            onChange={(e) => set('auth_scheme', e.target.value as AuthScheme)}
          >
            <option value="api_key">api_key</option>
            <option value="bearer">bearer</option>
            <option value="oauth2">oauth2 (MCP OAuth)</option>
          </select>
        </label>
        {form.auth_scheme === 'api_key' && (
          <label>
            API key header
            <input
              className="scr-input"
              value={form.api_key_header}
              placeholder="X-API-Key"
              onChange={(e) => set('api_key_header', e.target.value)}
            />
          </label>
        )}
        <label>
          Tool prefix category
          <input
            className="scr-input"
            maxLength={40}
            value={form.category}
            placeholder="Project Management"
            onChange={(e) => set('category', e.target.value)}
          />
        </label>
        <label>
          Sync credential (write-only, optional)
          <input
            className="scr-input"
            type="password"
            autoComplete="off"
            value={form.sync_credential}
            onChange={(e) => set('sync_credential', e.target.value)}
          />
        </label>
        <label>
          Vendor ToS URL (optional)
          <input
            className="scr-input"
            value={form.tos_url}
            onChange={(e) => set('tos_url', e.target.value)}
          />
        </label>
        <label>
          ToS note (optional)
          <input
            className="scr-input"
            value={form.tos_note}
            onChange={(e) => set('tos_note', e.target.value)}
          />
        </label>
        <label>
          Switch existing connector (optional)
          <input
            className="scr-input"
            value={form.connector_name}
            placeholder="existing OpenAPI connector name"
            onChange={(e) => set('connector_name', e.target.value)}
          />
        </label>
        {form.auth_scheme === 'oauth2' && (
          <>
            <label>
              OAuth client registration
              <select
                value={form.oauth_registration}
                onChange={(e) => set('oauth_registration', e.target.value as Registration)}
              >
                <option value="auto">auto (DCR, else client ID metadata document)</option>
                <option value="dcr">dcr</option>
                <option value="preregistered">preregistered (vendor-issued)</option>
                <option value="cimd">cimd</option>
              </select>
            </label>
            <label>
              OAuth client ID (vendor-issued, optional)
              <input
                className="scr-input"
                maxLength={512}
                value={form.oauth_client_id}
                onChange={(e) => set('oauth_client_id', e.target.value)}
              />
            </label>
            <label>
              OAuth client secret (write-only, optional)
              <input
                className="scr-input"
                type="password"
                autoComplete="off"
                value={form.oauth_client_secret}
                onChange={(e) => set('oauth_client_secret', e.target.value)}
              />
            </label>
            <label>
              Token endpoint auth method
              <select
                value={form.oauth_token_auth}
                onChange={(e) => set('oauth_token_auth', e.target.value as TokenAuth)}
              >
                <option value="">(default)</option>
                <option value="client_secret_post">client_secret_post</option>
                <option value="client_secret_basic">client_secret_basic</option>
                <option value="none">none</option>
              </select>
            </label>
            <label>
              Scopes override (space/comma separated, optional)
              <input
                className="scr-input"
                value={form.oauth_scopes}
                onChange={(e) => set('oauth_scopes', e.target.value)}
              />
            </label>
          </>
        )}
        <div className="scr-panel-actions">
          <button
            type="submit"
            className="screen-btn screen-btn-primary"
            disabled={!valid || register.isPending}
          >
            {register.isPending ? 'Registering…' : 'Register (dark)'}
          </button>
          {register.isSuccess && (
            <span className="scr-ok">
              Registered {register.data?.connector_name} (dark).
            </span>
          )}
        </div>
      </form>
      {register.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(register.error)}
        </p>
      )}
      {confirming && (
        <ConfirmDialog
          label="Confirm connector switch"
          confirmText="Switch"
          onCancel={() => setConfirming(false)}
          onConfirm={() => {
            setConfirming(false)
            register.mutate(registerBody(form))
          }}
        >
          <p>
            Switch the existing connector <strong>{form.connector_name.trim()}</strong> to this
            upstream? Its tools are deactivated until re-approved, and a changed auth scheme
            marks its connected accounts needs_reconnect.
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}

export function McpUpstreamsView() {
  const qc = useQueryClient()
  const [confirmResync, setConfirmResync] = useState(false)

  const query = useQuery<{ upstreams: Upstream[] }>({
    queryKey: UPSTREAMS_QUERY_KEY,
    queryFn: () => apiFetch('/admin/connectors/mcp'),
  })
  useForbiddenGuard(query.error)

  const resync = useMutation({
    mutationFn: () => apiFetch<ResyncAllResult>('/admin/mcp-upstreams/resync', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY }),
  })
  useWriteForbiddenGuard(resync.error)

  const upstreams = query.data?.upstreams ?? []
  const suggested = upstreams.filter((u) => u.resync_suggested_at).length

  return (
    <section className="scr" aria-labelledby="mcpu-heading">
      <div className="screen-head">
        <h2 id="mcpu-heading">MCP Upstreams</h2>
        <span className="scr-actions">
          {suggested > 0 && (
            <span className="scr-badge scr-badge--changed">
              {suggested} re-sync suggested
            </span>
          )}
          <button
            type="button"
            className="screen-btn"
            disabled={resync.isPending}
            onClick={() => setConfirmResync(true)}
          >
            {resync.isPending ? 'Re-syncing…' : 'Re-sync all live'}
          </button>
        </span>
      </div>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load upstream MCP connectors.
        </p>
      )}
      {resync.isError && (
        <p className="screen-error" role="alert">
          {titledErrorMessage(resync.error)}
        </p>
      )}
      {resync.isSuccess && resync.data && (
        <div className="scr-ok" aria-label="Re-sync result">
          Re-sync: {resync.data.synced} synced, {resync.data.failed} failed,{' '}
          {resync.data.skipped} skipped.
          <ul>
            {resync.data.upstreams.map((r) => (
              <li key={r.slug}>
                <span className="scr-mono">{r.slug}</span>: {r.status}
                {r.error ? ` — ${r.error}` : ''}
                {r.reason ? ` — ${r.reason}` : ''}
                {r.status === 'synced'
                  ? ` (new ${r.new?.length ?? 0}, changed ${r.changed?.length ?? 0}, suspended ${r.suspended?.length ?? 0}, removed ${r.removed?.length ?? 0})`
                  : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      <table className="scr-table" aria-label="Upstream MCP connectors">
        <thead>
          <tr>
            <th>Upstream</th>
            <th>URL</th>
            <th>Auth</th>
            <th>Exposure</th>
            <th>ToS review</th>
            <th>Last sync</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {upstreams.map((u) => (
            <tr key={u.slug}>
              <td>
                {u.display_name || u.connector_name}
                <div className="scr-muted scr-mono">{u.slug}</div>
                {u.resync_suggested_at && (
                  <span className="scr-badge scr-badge--changed">re-sync suggested</span>
                )}
              </td>
              <td className="scr-mono">
                {u.url}
                <div className="scr-muted">{u.transport}</div>
              </td>
              <td>
                {u.auth_scheme}
                <div className="scr-muted">
                  {u.has_sync_credential ? 'sync credential set' : 'no sync credential'}
                </div>
              </td>
              <td>
                <span className={`scr-badge scr-badge--${u.exposure === 'live' ? 'active' : 'pending'}`}>
                  {u.exposure}
                </span>
                <div className="scr-muted" aria-label={`Categories for ${u.slug}`}>
                  {(u.categories ?? []).length > 0 ? u.categories.join(', ') : 'no categories'}
                </div>
              </td>
              <td>{u.tos_reviewed_at ? fmtTime(u.tos_reviewed_at) : 'Not recorded'}</td>
              <td>
                {fmtTime(u.last_sync_at)}
                {u.last_sync_error && <div className="scr-err">{u.last_sync_error}</div>}
              </td>
              <td>
                <Link to={`/mcp-upstreams/${encodeURIComponent(u.slug)}`} aria-label={`Open ${u.slug}`}>
                  Review
                </Link>
              </td>
            </tr>
          ))}
          {!query.isLoading && upstreams.length === 0 && (
            <tr>
              <td colSpan={7} className="ws-empty">
                No upstream MCP connectors.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <RegisterPanel />
      <HostsPanel />

      {confirmResync && (
        <ConfirmDialog
          label="Confirm re-sync all"
          confirmText="Re-sync"
          onCancel={() => setConfirmResync(false)}
          onConfirm={() => {
            setConfirmResync(false)
            resync.mutate()
          }}
        >
          <p>
            Re-sync every <strong>live</strong> upstream now? New tools land pending, changes are
            held for review, and tools an upstream no longer lists are deactivated immediately.
          </p>
        </ConfirmDialog>
      )}
    </section>
  )
}
